import {MarkerFeature, MarkerGeometry, MarkerProperties} from "../../../../utils/models/MarkerFeature";
import {MutationTree} from "vuex";
import {PlanTrailState} from "./PlanTrailModule";
import {LineStringFeature} from "../../../../utils/models/LineStringFeature";
import {Route, RouteFactory} from "../../../../utils/models/Route";
import {StageDestination} from "../../services/stage-destinations";
import {closeDayGaps, remapByDay, renumbersAnyDay} from "../../utils/dayRenumber";
import {sanitizeCoords} from "../../../../utils/sanitizeCoords";
import { i18n } from "../../../../i18n";
import {applyJoinSpurTrims} from "../../../../utils/models/navigation/joinSpurs";
import {JoinTarget, JoinTurn, applyJoinTurns} from "../../../../utils/models/navigation/joinTurns";
import {MatchRun, MatchedTurn, applyMatchedTurns} from "../../../../utils/models/navigation/matchedTurns";
import {repairRouteElevations, syncMarkerElevationsFromLines} from "../../../../utils/elevation/repairElevations";
import { soleRouteKey } from "../../utils/soleRoute";

// Translated when called (never at module load) so names and prompts follow the page's locale.
const t = (key: string, named: Record<string, unknown> = {}): string => i18n.global.t(key, named);

const generateHighlightImageClientId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `highlight_image_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const normaliseHighlightImageUploads = (uploads?: any[]) => {
  if (!uploads || uploads.length === 0) {
    return undefined;
  }

  return uploads
    .map((entry) => {
      if (!entry) return null;

      if (typeof entry === 'string') {
        return {
          signedId: entry,
          clientId: generateHighlightImageClientId()
        };
      }

      const signedId = entry.signedId || entry.signed_id;
      if (!signedId) {
        return null;
      }
      const clientId = entry.clientId || entry.client_id || generateHighlightImageClientId();
      return { signedId, clientId };
    })
    .filter(Boolean) as Array<{ signedId: string; clientId: string }>;
};

const normaliseExistingHighlightImages = (images?: any[]) => {
  if (!images || images.length === 0) {
    return [] as Array<{
      id?: string | number;
      clientId: string;
      file?: {
        lgUrl?: string;
        smUrl?: string;
        mdUrl?: string;
        url?: string;
      };
      _destroy?: boolean;
    }>;
  }

  return images.map((image: any) => {
    if (!image) return null;
    const clientId = image.clientId || image.client_id || generateHighlightImageClientId();
    const normalisedImage = {
      ...image,
      clientId,
      _destroy: Boolean(image._destroy)
    };
    return normalisedImage;
  }).filter(Boolean) as Array<{
    id?: string | number;
    clientId: string;
    file?: {
      lgUrl?: string;
      smUrl?: string;
      mdUrl?: string;
      url?: string;
    };
    _destroy?: boolean;
  }>;
};

// New media uploads (audio recordings/files) awaiting attachment. Each carries
// its mediaType so the wire contract stays explicit end to end.
const normaliseHighlightMediaUploads = (uploads?: any[]) => {
  if (!uploads || uploads.length === 0) {
    return undefined;
  }

  return uploads
    .map((entry) => {
      if (!entry) return null;
      const signedId = entry.signedId || entry.signed_id;
      if (!signedId) return null;
      return {
        signedId,
        clientId: entry.clientId || entry.client_id || generateHighlightImageClientId(),
        mediaType: entry.mediaType || entry.media_type || 'audio',
        title: entry.title,
        previewUrl: entry.previewUrl
      };
    })
    .filter(Boolean) as Array<{ signedId: string; clientId: string; mediaType: string; title?: string; previewUrl?: string }>;
};

// Media already persisted server-side (have an id + url). Removal is by omission
// from this list (replace-state on the server), so a Remove in the UI drops the
// entry rather than flagging _destroy.
const normaliseExistingHighlightMedia = (media?: any[]) => {
  if (!media || media.length === 0) {
    return [] as Array<{ id?: string | number; clientId: string; mediaType: string; contentType?: string; title?: string; description?: string; url?: string; _destroy?: boolean }>;
  }

  return media.map((item: any) => {
    if (!item) return null;
    return {
      ...item,
      clientId: item.clientId || item.client_id || generateHighlightImageClientId(),
      mediaType: item.mediaType || item.media_type || 'audio',
      _destroy: Boolean(item._destroy)
    };
  }).filter(Boolean) as Array<{ id?: string | number; clientId: string; mediaType: string; contentType?: string; title?: string; description?: string; url?: string; _destroy?: boolean }>;
};

const mutations: MutationTree<PlanTrailState> = {
  setFirstSymbolLayerId(state, layerId: string | null) {
    state.firstSymbolLayerId = layerId;
  },
  setTitle(state, title: string) {
    state.title = title;
  },
  setDescription(state, description: string) {
    state.description = description;
  },
  setSubmitForOfficialApproval(state, value: boolean) {
    state.submitForOfficialApproval = value;
  },
  setMakeOfficial(state, value: boolean) {
    state.makeOfficial = value;
  },
  setGenerateAiDescription(state, value: boolean) {
    state.generateAiDescription = value;
  },
  setAvailableFeatureTags(state, tags: string[]) {
    state.availableFeatureTags = [...tags];
    if (state.selectedFeatureTags.length > 0) {
      state.selectedFeatureTags = state.selectedFeatureTags.filter(tag => state.availableFeatureTags.includes(tag));
    }
  },
  setAvailableActivityTags(state, tags: string[]) {
    state.availableActivityTags = [...tags];
    if (state.selectedActivityTags.length > 0) {
      state.selectedActivityTags = state.selectedActivityTags.filter(tag => state.availableActivityTags.includes(tag));
    }
  },
  setSelectedFeatureTags(state, tags: string[]) {
    const uniqueTags = Array.from(new Set(tags));
    state.selectedFeatureTags = state.availableFeatureTags.length > 0
      ? uniqueTags.filter(tag => state.availableFeatureTags.includes(tag))
      : uniqueTags;
  },
  setSelectedActivityTags(state, tags: string[]) {
    const uniqueTags = Array.from(new Set(tags));
    state.selectedActivityTags = state.availableActivityTags.length > 0
      ? uniqueTags.filter(tag => state.availableActivityTags.includes(tag))
      : uniqueTags;
  },
  setAvailableCustomTags(state, tags: string[]) {
    state.availableCustomTags = [...tags];
  },
  setSelectedCustomTags(state, tags: string[]) {
    // Custom tags are user-created and free-form, so unlike feature/activity
    // tags they are NOT filtered against the available catalog — a freshly
    // typed tag must survive. Trim and de-dupe only.
    const cleaned = tags.map(tag => tag.trim()).filter(tag => tag.length > 0);
    state.selectedCustomTags = Array.from(new Set(cleaned));
  },
  setDifficultyOverride(state, difficulty: string | null) {
    state.difficultyOverride = difficulty;
  },
  setError(state, error: string) {
    state.error = error
  },
  clearError(state) {
    state.error = null
  },
  setMiniCoordsBar(state, miniCoordsBarString: string) {
    state.miniCoordsBar = miniCoordsBarString
  },
  setState(state, newState) {
    state = {...newState}
  },
  setZoom(state, zoom: number) {
    state.zoom = zoom
  },
  setCenter(state, center: number[]) {
    state.center = center
  },
  setMarkers(state, markers: MarkerFeature[]) {
    // Update the active route only
    if (state.activeRouteKey && state.routes[state.activeRouteKey]) {
      state.routes[state.activeRouteKey].markers = [...markers];
      state.routes[state.activeRouteKey].updated_at = new Date();
    }
  },
  /**
   * Replace one route's line strings, named by its key rather than whichever route is active.
   *
   * `setLineStrings` below writes to the active route only, which silently does nothing for
   * any other route. Committing this — rather than mutating a line string in place — is also
   * what makes the IndexedDB draft save, since the autosave subscriber watches mutation types.
   */
  setRouteLineStrings(state, payload: { routeKey: string; lineStrings: LineStringFeature[] }) {
    const route = state.routes[payload.routeKey];
    if (!route) return;
    route.lineStrings = [...payload.lineStrings];
    route.updated_at = new Date();
  },
  setLineStrings(state, lineStrings: LineStringFeature[]) {
    // Update the active route only
    if (state.activeRouteKey && state.routes[state.activeRouteKey]) {
      state.routes[state.activeRouteKey].lineStrings = [...lineStrings];
      state.routes[state.activeRouteKey].updated_at = new Date();
    }
  },
  updateMarker(state, marker: MarkerFeature) {
    // Prefer the active route, but fall back to whichever route owns the
    // marker — edits are allowed on non-active routes (whole-trail editing)
    const candidateKeys = state.activeRouteKey && state.routes[state.activeRouteKey]
      ? [state.activeRouteKey, ...Object.keys(state.routes).filter(k => k !== state.activeRouteKey)]
      : Object.keys(state.routes);

    for (const key of candidateKeys) {
      const route = state.routes[key];
      const routeIndex = route.markers.findIndex(m => m.markerProperties.id === marker.markerProperties.id);
      if (routeIndex !== -1) {
        route.markers[routeIndex] = marker;
        route.updated_at = new Date();
        return;
      }
    }
  },
  addMarker(state, marker: MarkerFeature) {
    // Add to active route only
    if (state.activeRouteKey && state.routes[state.activeRouteKey]) {
      state.routes[state.activeRouteKey].markers.push(marker);
      state.routes[state.activeRouteKey].updated_at = new Date();
    }
  },
  addMarkerObj(state, markerDetails: {lng: number, lat: number, id: string, isSnap: boolean}): MarkerFeature {
    const marker = new MarkerFeature(
      new MarkerGeometry([markerDetails.lng, markerDetails.lat] as number[]),
      new MarkerProperties(markerDetails.id, markerDetails.isSnap, Date.now().toString(), 0)
    )
    // Add to active route only
    if (state.activeRouteKey && state.routes[state.activeRouteKey]) {
      state.routes[state.activeRouteKey].markers.push(marker);
      state.routes[state.activeRouteKey].updated_at = new Date();
    }
    return marker
  },
  insertMarkerObj(state, payload: {lng: number, lat: number, id: string, isSnap: boolean, index: number}): MarkerFeature {
    const marker = new MarkerFeature(
      new MarkerGeometry([payload.lng, payload.lat] as number[]),
      new MarkerProperties(payload.id, payload.isSnap, Date.now().toString(), 0)
    )
    // Insert in active route only
    if (state.activeRouteKey && state.routes[state.activeRouteKey]) {
      state.routes[state.activeRouteKey].markers.splice(payload.index, 0, marker);
      state.routes[state.activeRouteKey].updated_at = new Date();
    }
    return marker
  },
  removeMarker(state, marker: MarkerFeature) {
    // Update active route only
    if (state.activeRouteKey && state.routes[state.activeRouteKey]) {
      const activeRoute = state.routes[state.activeRouteKey];
      const routeMarkerIndex = activeRoute.markers.findIndex(m => m.markerProperties.id === marker.markerProperties.id);
      if (routeMarkerIndex !== -1) {
        activeRoute.markers.splice(routeMarkerIndex, 1);
        activeRoute.updated_at = new Date();
      }
    }
  },
  addLineStrings(state, lineStrings: LineStringFeature[]) {
    // Update the active route only
    if (state.activeRouteKey && state.routes[state.activeRouteKey]) {
      state.routes[state.activeRouteKey].lineStrings = [...lineStrings];
      state.routes[state.activeRouteKey].updated_at = new Date();
    }
  },
  addLineString(state, lineString: LineStringFeature) {
    // Add to active route only
    if (state.activeRouteKey && state.routes[state.activeRouteKey]) {
      state.routes[state.activeRouteKey].lineStrings.push(lineString);
      state.routes[state.activeRouteKey].updated_at = new Date();
    }
  },
  mergeLineStrings(state, payload: {lineString1Index: number, lineString2Index: number}) {
    // Work with active route only
    if (!state.activeRouteKey || !state.routes[state.activeRouteKey]) {
      return;
    }
    const activeRoute = state.routes[state.activeRouteKey];
    let lineString1 = activeRoute.lineStrings[payload.lineString1Index]
    let lineString2 = activeRoute.lineStrings[payload.lineString2Index]

    // Get the coordinates from both lineStrings
    const coords1 = lineString1.lineStringGeometry.coordinates;
    const coords2 = lineString2.lineStringGeometry.coordinates;

    // Merge coordinates, removing the duplicate point at the junction
    let mergedCoordinates: number[][];

    if (coords1.length > 0 && coords2.length > 0) {
      // Check if the last point of lineString1 equals the first point of lineString2
      const lastPoint1 = coords1[coords1.length - 1];
      const firstPoint2 = coords2[0];

      if (lastPoint1[0] === firstPoint2[0] && lastPoint1[1] === firstPoint2[1]) {
        // Skip the first point of lineString2 to avoid duplicate
        mergedCoordinates = coords1.concat(coords2.slice(1));
      } else {
        // No duplicate found, concat normally
        mergedCoordinates = coords1.concat(coords2);
      }
    } else {
      // One of the lineStrings is empty, just concat
      mergedCoordinates = coords1.concat(coords2);
    }

    let mergedLineString = LineStringFeature.coordsOnlyFeature(mergedCoordinates);

    // Remove the lineString2Index lineString from active route
    activeRoute.lineStrings.splice(payload.lineString2Index, 1)
    activeRoute.lineStrings[payload.lineString1Index] = mergedLineString
    activeRoute.updated_at = new Date();
  },
  removeLastLineString(state) {
    // Remove from active route only
    if (state.activeRouteKey && state.routes[state.activeRouteKey]) {
      const activeRoute = state.routes[state.activeRouteKey];
      if (activeRoute.lineStrings.length > 0) {
        activeRoute.lineStrings.pop();
        activeRoute.updated_at = new Date();
      }
    }
  },
  removeFirstLineString(state) {
    // Remove from active route only
    if (state.activeRouteKey && state.routes[state.activeRouteKey]) {
      const activeRoute = state.routes[state.activeRouteKey];
      if (activeRoute.lineStrings.length > 0) {
        activeRoute.lineStrings.shift();
        activeRoute.updated_at = new Date();
      }
    }
  },
  /** A provisional line no real route is coming for stops reading as "still routing" (see utils/pendingSnap.ts). */
  writeJoinTurns(state, { routeKey, target, turns }: { routeKey: string; target: JoinTarget; turns: JoinTurn[] | null }) {
    const route = state.routes[routeKey];
    if (route && applyJoinTurns(route.lineStrings, target, turns)) route.updated_at = new Date();
  },

  writeMatchedTurns(state, { routeKey, ids, run, turns }: { routeKey: string; ids: (string | null)[]; run: MatchRun; turns: MatchedTurn[] }) {
    const route = state.routes[routeKey];
    if (route && applyMatchedTurns(route.lineStrings, ids, run, turns)) route.updated_at = new Date();
  },

  settleLineString(state, lineStringId: string) {
    Object.values(state.routes).forEach(route => {
      route.lineStrings.forEach(lineString => {
        const props = lineString?.lineStringProperties as any;
        if (props?.id === lineStringId && props.pendingSnap) props.pendingSnap = false;
      });
    });
  },

  addLineStringAtIndex(state, payload: {lineString: LineStringFeature, index: number, routeKey?: string}) {
    // Target route: explicit routeKey (whole-trail editing) or the active route
    const targetKey = payload.routeKey ?? state.activeRouteKey;
    if (targetKey && state.routes[targetKey]) {
      const activeRoute = state.routes[targetKey];
      if (payload.index == activeRoute.lineStrings.length) {
        activeRoute.lineStrings.push(payload.lineString);
      } else {
        activeRoute.lineStrings.splice(payload.index, 0, payload.lineString);
      }
      activeRoute.updated_at = new Date();
      state._statsVersion++;
    }
  },
  removeLineString(state, lineString: LineStringFeature) {
    // Remove from active route only
    if (state.activeRouteKey && state.routes[state.activeRouteKey]) {
      const activeRoute = state.routes[state.activeRouteKey];
      const index = activeRoute.lineStrings.indexOf(lineString);
      if (index > -1) {
        activeRoute.lineStrings.splice(index, 1);
        activeRoute.updated_at = new Date();
        state._statsVersion++;
      }
    }
  },
  removeLineStringAtIndex(state, payload: number | { index: number; routeKey?: string }): LineStringFeature | null {
    // Accepts a bare index (active route, legacy) or { index, routeKey }
    const index = typeof payload === 'number' ? payload : payload.index;
    const targetKey = (typeof payload === 'object' && payload.routeKey) ? payload.routeKey : state.activeRouteKey;
    if (targetKey && state.routes[targetKey]) {
      const activeRoute = state.routes[targetKey];
      if (activeRoute.lineStrings[index]) {
        const removed = activeRoute.lineStrings.splice(index, 1)[0];
        activeRoute.updated_at = new Date();
        state._statsVersion++;
        return removed;
      }
    }
    return null;
  },
  removeAllLineStrings(state) {
    // Clear linestrings from active route only
    if (state.activeRouteKey && state.routes[state.activeRouteKey]) {
      state.routes[state.activeRouteKey].lineStrings = [];
      state.routes[state.activeRouteKey].updated_at = new Date();
      state._statsVersion++;
    }
  },
  removeAllMarkers(state) {
    // Clear markers from active route only
    if (state.activeRouteKey && state.routes[state.activeRouteKey]) {
      state.routes[state.activeRouteKey].markers = [];
      state.routes[state.activeRouteKey].updated_at = new Date();
    }
  },
  updateUndo(state) {
    // Update per-route undo state
    if (state.activeRouteKey && state.routes[state.activeRouteKey]) {
      const activeRoute = state.routes[state.activeRouteKey];
      activeRoute.undoMarkers.push(JSON.parse(JSON.stringify(activeRoute.markers)));
      activeRoute.undoLineStrings.push(JSON.parse(JSON.stringify(activeRoute.lineStrings)));

      // Limit undo history size
      if (activeRoute.undoMarkers.length > 80) {
        activeRoute.undoMarkers.shift();
        activeRoute.undoLineStrings.shift();
      }
    }
  },
  updateRouteUndo(state, routeKey: string) {
    const route = state.routes[routeKey];
    if (!route) return;

    route.undoMarkers.push(JSON.parse(JSON.stringify(route.markers)));
    route.undoLineStrings.push(JSON.parse(JSON.stringify(route.lineStrings)));

    // Limit undo history size
    if (route.undoMarkers.length > 80) {
      route.undoMarkers.shift();
      route.undoLineStrings.shift();
    }

    // Clear redo stack when new action is performed
    route.redoMarkers = [];
    route.redoLineStrings = [];
  },
  updateRedo(state) {
    // Update per-route redo state
    if (state.activeRouteKey && state.routes[state.activeRouteKey]) {
      const activeRoute = state.routes[state.activeRouteKey];
      activeRoute.redoMarkers.push(JSON.parse(JSON.stringify(activeRoute.markers)));
      activeRoute.redoLineStrings.push(JSON.parse(JSON.stringify(activeRoute.lineStrings)));
    }
  },
  popRedo(state) {
    // Update per-route pop only (legacy state removed)
    if (state.activeRouteKey && state.routes[state.activeRouteKey]) {
      const activeRoute = state.routes[state.activeRouteKey];
      activeRoute.redoMarkers.pop();
      activeRoute.redoLineStrings.pop();
    }
  },
  popUndo(state) {
    // Update per-route pop only (legacy state removed)
    if (state.activeRouteKey && state.routes[state.activeRouteKey]) {
      const activeRoute = state.routes[state.activeRouteKey];
      activeRoute.undoMarkers.pop();
      activeRoute.undoLineStrings.pop();
    }
  },
  clearRedo(state) {
    // Update per-route clear only (legacy state removed)
    if (state.activeRouteKey && state.routes[state.activeRouteKey]) {
      const activeRoute = state.routes[state.activeRouteKey];
      activeRoute.redoMarkers = [];
      activeRoute.redoLineStrings = [];
    }
  },
  clearUndo(state) {
    // Update per-route clear only (legacy state removed)
    if (state.activeRouteKey && state.routes[state.activeRouteKey]) {
      const activeRoute = state.routes[state.activeRouteKey];
      activeRoute.undoMarkers = [];
      activeRoute.undoLineStrings = [];
    }
  },
  setToLastUndo(state) {
    // Update from per-route undo only (legacy state removed)
    if (state.activeRouteKey && state.routes[state.activeRouteKey]) {
      const activeRoute = state.routes[state.activeRouteKey];
      if (activeRoute.undoMarkers.length > 0) {
        activeRoute.markers = [...activeRoute.undoMarkers[activeRoute.undoMarkers.length - 1]];
        activeRoute.lineStrings = [...activeRoute.undoLineStrings[activeRoute.undoLineStrings.length - 1]];
      }
    }
  },
  toggleSnap(state) {
    state.isSnap = !state.isSnap
  },
  setPlanMode(state, planMode: boolean) {
    state.planMode = planMode
  },
  setRoutePreference(state, value) {
    state.routePreference = value;
  },

  // Route management mutations (key-value approach)
  addRoute(state, route: any) {
    const routeKey = route.id;
    // Calculate position if not provided
    const position = route.position !== undefined ? route.position : Object.keys(state.routes).length;

    state.routes[routeKey] = {
      id: route.id,
      name: route.name,
      position: position,
      markers: [...route.markers],
      lineStrings: [...route.lineStrings],
      undoMarkers: [],
      redoMarkers: [],
      undoLineStrings: [],
      redoLineStrings: [],
      created_at: route.created_at || new Date(),
      updated_at: route.updated_at || new Date(),
      route_style: route.route_style,
      day: route.day,
      // Preserve reference fields if present
      ...(route.plannedTrailId && {
        plannedTrailId: route.plannedTrailId,
        isReference: route.isReference,
        referencedHikes: route.referencedHikes || []
      })
    };

    if (!state.activeRouteKey) {
      state.activeRouteKey = routeKey;
    }
    state._statsVersion++;
  },

  removeRoute(state, routeId: string) {
    // Look up by direct key first; fall back to scanning by route.id in case of legacy
    // mismatches where state.routes was keyed differently from the route's own id.
    let routeKey: string | undefined = state.routes[routeId] ? routeId : undefined;
    if (!routeKey) {
      routeKey = Object.keys(state.routes).find(k => state.routes[k]?.id === routeId);
    }

    if (routeKey && state.routes[routeKey]) {
      delete state.routes[routeKey];
      if (state.activeRouteKey === routeKey) {
        const remainingKeys = Object.keys(state.routes);
        if (remainingKeys.length > 0) {
          state.activeRouteKey = remainingKeys[0];
        } else {
          state.activeRouteKey = null;
        }
      }
      // Down to one route: it's active, whichever one was before (see soleRouteKey).
      state.activeRouteKey = soleRouteKey(state.routes) ?? state.activeRouteKey;
      state._statsVersion++;
    }
  },

  setActiveRoute(state, routeId: string) {
    const routeKey = routeId;
    if (state.routes[routeKey]) {
      state.activeRouteKey = routeKey;
    }
  },

  // Deactivate — no active day/route. The whole trail renders full-colour
  // and editing works via auto-activate-on-touch (click a line or stage
  // marker to activate that route mid-gesture). A single-route plan has no
  // overview to go back to, so its route stays active (see soleRouteKey).
  clearActiveRoute(state) {
    state.activeRouteKey = soleRouteKey(state.routes);
  },

  updateRouteName(state, payload: {routeId: string, name: string}) {
    const routeKey = payload.routeId;
    if (state.routes[routeKey]) {
      state.routes[routeKey].name = payload.name;
      state.routes[routeKey].updated_at = new Date();
    }
  },

  createNewRoute(state, name?: string) {
    const routeId = `route_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const routeName = name || t('plan_trail.mutations.new_route');

    // Calculate the next position based on existing routes
    const routeCount = Object.keys(state.routes).length;

    // Determine which day to assign the route to
    let day: number | undefined;
    if (routeCount === 0) {
      // First route, no day needed
      day = undefined;
      
    } else {
      // For any subsequent routes, ensure first route has day 1
      const existingRoutes = Object.values(state.routes);
      
      // If this is the second route being added
      if (routeCount === 1) {
        const existingRoute = existingRoutes[0];
        
        if (!existingRoute.day || existingRoute.day === 1) {
          existingRoute.day = 1;
          existingRoute.updated_at = new Date();
          // New route goes to day 2
          day = 2;
        } else {
          // If existing route is not on day 1, put new route on day 1
          day = 1;
        }
      } else {
        // For 3rd+ routes, find the highest day and increment
        const days = existingRoutes.map(r => r.day || 1);
        const maxDay = Math.max(...days);
        day = maxDay + 1;
      }
    }

    const newRoute = {
      id: routeId,
      name: routeName,
      position: routeCount,
      markers: [],
      lineStrings: [],
      undoMarkers: [],
      redoMarkers: [],
      undoLineStrings: [],
      redoLineStrings: [],
      created_at: new Date(),
      updated_at: new Date(),
      route_style: 'route', // Default to 'route'
      day
    };

    state.routes[routeId] = newRoute;
    state.activeRouteKey = routeId;
  },

  duplicateRoute(state, routeId: string) {
    const sourceKey = routeId;
    if (state.routes[sourceKey]) {
      const newRouteId = `route_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const sourceRoute = state.routes[sourceKey];

      // Calculate the next position based on existing routes
      const routeCount = Object.keys(state.routes).length;

      state.routes[newRouteId] = {
        id: newRouteId,
        name: t('plan_trail.mutations.route_copy_name', { name: sourceRoute.name }),
        position: routeCount,
        markers: [...sourceRoute.markers],
        lineStrings: [...sourceRoute.lineStrings],
        undoMarkers: [],
        redoMarkers: [],
        undoLineStrings: [],
        redoLineStrings: [],
        created_at: new Date(),
        updated_at: new Date()
      };
    }
  },

  updateRoutePositions(state, reorderedRouteIds: string[]) {
    // Update the position of each route based on the new order
    reorderedRouteIds.forEach((routeId, index) => {
      if (state.routes[routeId]) {
        state.routes[routeId].position = index;
        state.routes[routeId].updated_at = new Date();
      }
    });
  },

  migrateToMultiRoute(state) {
    // Migrate to multi-route structure if no routes exist
    if (Object.keys(state.routes).length === 0) {
      // Create a default "New route"
      const routeId = `route_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      state.routes[routeId] = {
        id: routeId,
        name: t('plan_trail.mutations.new_route'),
        position: 0,
        markers: [],
        lineStrings: [],
        undoMarkers: [],
        redoMarkers: [],
        undoLineStrings: [],
        redoLineStrings: [],
        created_at: new Date(),
        updated_at: new Date()
      };
      state.activeRouteKey = routeId;
    }
  },

  setHoverPoint(state, point: { lng: number; lat: number; lineStringId: string; insertIndex: number } | null) {
    state.hoverPoint = point;
  },

  setNearLine(state, near: boolean) {
    state.nearLine = near;
  },

  setMapHoverDistance(state, distance: number | null) {
    state.mapHoverDistance = distance;
  },

  // Update route style
  updateRouteStyle(state, { routeId, style }: { routeId: string; style: 'route' | 'alt' | 'spur' | 'transport' }) {
    const routeKey = Object.keys(state.routes).find(key => state.routes[key].id === routeId);
    if (routeKey && state.routes[routeKey]) {
      state.routes[routeKey].route_style = style;
      state.routes[routeKey].updated_at = new Date();
    }
  },

  // Drag preview mutations
  startDragPreview(state, payload: { markerIndex: number; routeId: string; position: [number, number] }) {
    state.dragPreview = {
      isDragging: true,
      markerIndex: payload.markerIndex,
      routeId: payload.routeId,
      connector: null,
      originalPosition: payload.position,
      previewPosition: payload.position,
      snappedPosition: null,
      previewLineStrings: [],
      pendingRequest: null,
    };
  },

  // Start a stage-connector drag — moves two routes' endpoints together
  startConnectorDragPreview(state, payload: { prevRouteKey: string; nextRouteKey: string; position: [number, number] }) {
    state.dragPreview = {
      isDragging: true,
      markerIndex: null,
      routeId: null,
      connector: { prevRouteKey: payload.prevRouteKey, nextRouteKey: payload.nextRouteKey },
      originalPosition: payload.position,
      previewPosition: payload.position,
      snappedPosition: null,
      previewLineStrings: [],
      pendingRequest: null,
    };
  },

  updateDragPreviewPosition(state, position: [number, number]) {
    if (state.dragPreview.isDragging) {
      state.dragPreview.previewPosition = position;
    }
  },

  updateDragPreviewSnappedPosition(state, snappedPosition: [number, number] | null) {
    if (state.dragPreview.isDragging) {
      state.dragPreview.snappedPosition = snappedPosition;
    }
  },

  updateDragPreviewLineStrings(state, lineStrings: LineStringFeature[]) {
    if (state.dragPreview.isDragging) {
      state.dragPreview.previewLineStrings = lineStrings;
    }
  },

  setDragPreviewPendingRequest(state, controller: AbortController | null) {
    state.dragPreview.pendingRequest = controller;
  },

  endDragPreview(state) {
    // Cancel any pending request
    if (state.dragPreview.pendingRequest) {
      state.dragPreview.pendingRequest.abort();
    }

    // Reset preview state
    state.dragPreview = {
      isDragging: false,
      markerIndex: null,
      routeId: null,
      connector: null,
      originalPosition: null,
      previewPosition: null,
      snappedPosition: null,
      previewLineStrings: [],
      pendingRequest: null,
    };
  },

  updateRouteDay(state, payload: { routeId: string; day: number | undefined }) {
    const routeKey = payload.routeId;
    if (state.routes[routeKey]) {
      state.routes[routeKey].day = payload.day;
      state.routes[routeKey].updated_at = new Date();
    }
  },

  setDayInfo(state, dayInfo: { [day: number]: { name: string; description: string } }) {
    state.dayInfo = { ...dayInfo };
  },

  // After a route is deleted, close any gap it left in the day numbers — see
  // utils/dayRenumber.ts. Day names and stage destinations move with their day.
  closeDayGaps(state) {
    const map = closeDayGaps(Object.values(state.routes).map(r => r.day));
    if (!renumbersAnyDay(map)) return;
    Object.values(state.routes).forEach(route => {
      if (typeof route.day === 'number' && map[route.day] !== route.day) {
        route.day = map[route.day];
        route.updated_at = new Date();
      }
    });
    state.dayInfo = remapByDay(state.dayInfo, map);
    state.stageDestinations = remapByDay(state.stageDestinations, map);
    state._statsVersion++;
  },

  setStageDestination(state, payload: { dayNumber: number; destination: StageDestination }) {
    state.stageDestinations = {
      ...state.stageDestinations,
      [payload.dayNumber]: { ...payload.destination },
    };
  },

  removeStageDestination(state, dayNumber: number) {
    if (!(dayNumber in state.stageDestinations)) return;
    const next = { ...state.stageDestinations };
    delete next[dayNumber];
    state.stageDestinations = next;
  },

  // The trail's overall START point (the very first marker of the whole
  // plan) attached to a map item — mirrors setStageDestination/
  // removeStageDestination, but singular: the start isn't "day 0's end", so
  // it has no day-keyed slot of its own. See services/stage-destinations.ts.
  setTrailStartDestination(state, destination: StageDestination) {
    state.trailStartDestination = { ...destination };
  },

  removeTrailStartDestination(state) {
    state.trailStartDestination = null;
  },

  updateRouteDaysAndPositions(state, payload: { routesByDay: { [day: number]: string[] }, noDay: string[] }) {

    // Update positions and days for all routes
    let position = 0;
    
    // First handle routes without days (if only one route total)
    
    payload.noDay.forEach((routeId) => {
      if (state.routes[routeId]) {
        
        state.routes[routeId].position = position++;
        state.routes[routeId].day = undefined;
        state.routes[routeId].updated_at = new Date();
      }
    });
    
    // Then handle routes with days
    const sortedDays = Object.keys(payload.routesByDay).sort((a, b) => parseInt(a) - parseInt(b));
    
    sortedDays.forEach(day => {
      const dayNum = parseInt(day);
      
      payload.routesByDay[dayNum].forEach((routeId) => {
        if (state.routes[routeId]) {
          
          state.routes[routeId].position = position++;
          state.routes[routeId].day = dayNum;
          state.routes[routeId].updated_at = new Date();
        } else {
          console.warn(`Route ${routeId} not found in state!`);
        }
      });
    });
    
    
  },
  
  // Hike highlights mutations
  addHikeHighlight(state, highlight: {
    type: string;
    title: string;
    description: string;
    coordinates: [number, number];
    dayNumber?: number;
    imageSignedIds?: Array<{ signedId: string; clientId: string }> | string[];
    imagePreviewUrls?: string[];
    images?: Array<{
      id?: string | number;
      clientId?: string;
      file?: {
        lgUrl?: string;
        smUrl?: string;
        mdUrl?: string;
        url?: string;
      };
      _destroy?: boolean;
    }>;
  }) {
    // Generate a unique client ID
    const clientId = `highlight_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const normalisedUploads = normaliseHighlightImageUploads(highlight.imageSignedIds);
    state.hikeHighlights.push({
      clientId,
      ...highlight,
      imageSignedIds: normalisedUploads,
      imagePreviewUrls: highlight.imagePreviewUrls || [],
      images: normaliseExistingHighlightImages(highlight.images),
      mediaSignedIds: normaliseHighlightMediaUploads((highlight as any).mediaSignedIds),
      media: normaliseExistingHighlightMedia((highlight as any).media)
    });
  },
  
  updateHikeHighlight(state, { clientId, updates }: { clientId: string; updates: Partial<{
    type: string;
    title: string;
    description: string;
    coordinates: [number, number];
    dayNumber?: number;
    imageSignedIds?: Array<{ signedId: string; clientId: string }> | string[];
    imagePreviewUrls?: string[];
    images?: Array<{
      id?: string | number;
      clientId?: string;
      file?: {
        lgUrl?: string;
        smUrl?: string;
        mdUrl?: string;
        url?: string;
      };
      _destroy?: boolean;
    }>;
  }> }) {
    const index = state.hikeHighlights.findIndex(h => h.clientId === clientId);
    if (index !== -1) {
      const normalisedUploads = normaliseHighlightImageUploads(updates.imageSignedIds);
      const currentHighlight = state.hikeHighlights[index];

      const nextHighlight = {
        ...currentHighlight,
        ...updates
      } as typeof currentHighlight;

      if (updates.imageSignedIds !== undefined) {
        nextHighlight.imageSignedIds = normalisedUploads;
      }

      if (updates.imagePreviewUrls !== undefined) {
        nextHighlight.imagePreviewUrls = updates.imagePreviewUrls;
      }

      if (updates.images !== undefined) {
        nextHighlight.images = normaliseExistingHighlightImages(updates.images);
      }

      if ((updates as any).mediaSignedIds !== undefined) {
        nextHighlight.mediaSignedIds = normaliseHighlightMediaUploads((updates as any).mediaSignedIds);
      }

      if ((updates as any).media !== undefined) {
        nextHighlight.media = normaliseExistingHighlightMedia((updates as any).media);
      }

      state.hikeHighlights[index] = nextHighlight;
    }
  },
  
  removeHikeHighlight(state, clientId: string) {
    state.hikeHighlights = state.hikeHighlights.filter(h => h.clientId !== clientId);
  },

  setHikeHighlights(state, highlights: Array<{
    clientId: string;
    type: string;
    title: string;
    description: string;
    coordinates: [number, number];
    dayNumber?: number;
    imageSignedIds?: Array<{ signedId: string; clientId: string }> | string[];
    imagePreviewUrls?: string[];
    images?: Array<{
      id?: string | number;
      clientId?: string;
      file?: {
        lgUrl?: string;
        smUrl?: string;
        mdUrl?: string;
        url?: string;
      };
      _destroy?: boolean;
    }>;
  }>) {
    state.hikeHighlights = highlights.map(highlight => ({
      ...highlight,
      imageSignedIds: normaliseHighlightImageUploads(highlight.imageSignedIds),
      imagePreviewUrls: highlight.imagePreviewUrls || [],
      images: normaliseExistingHighlightImages(highlight.images),
      mediaSignedIds: normaliseHighlightMediaUploads((highlight as any).mediaSignedIds),
      media: normaliseExistingHighlightMedia((highlight as any).media)
    }));
  },

  markPhotoForRemoval(state, clientId: string) {
    if (!state.photosMarkedForRemoval.includes(clientId)) {
      state.photosMarkedForRemoval.push(clientId);
    }
  },

  unmarkPhotoForRemoval(state, clientId: string) {
    state.photosMarkedForRemoval = state.photosMarkedForRemoval.filter(id => id !== clientId);
  },

  setAreaSelecting(state, value: boolean) {
    state.isAreaSelecting = value;
  },
  setAreaSelectedMarkers(state, markers: Array<{ routeKey: string; markerIndex: number }>) {
    state.areaSelectedMarkers = markers;
  },
  clearAreaSelection(state) {
    state.isAreaSelecting = false;
    state.areaSelectedMarkers = [];
  },
  removeExistingHikeImage(state, clientId: string) {
    state.existingHikeImages = state.existingHikeImages.filter(image => image.clientId !== clientId);
  },

  reorderPhotos(state, newPhotosIds: string[]) {
    state.photosIds = newPhotosIds;
  },

  // Route validation and cleanup mutations
  cleanupRouteLineStrings(state, { routeKey, expectedLineStrings }: { routeKey: string, expectedLineStrings: number }) {
    const route = state.routes[routeKey];
    if (!route) return;
    
    // Remove excess linestrings
    if (route.lineStrings.length > expectedLineStrings) {
      route.lineStrings.splice(expectedLineStrings);
      route.updated_at = new Date();
    }
  },
  
  validateAndCleanAllRoutes(state) {
    // Bump stats version — this mutation runs before every map render
    state._statsVersion++;
    Object.keys(state.routes).forEach(routeKey => {
      const route = state.routes[routeKey];
      const markerCount = route.markers.length;
      const expectedLineStrings = Math.max(0, markerCount - 1);

      // Clean up linestrings to match expected count
      if (route.lineStrings.length !== expectedLineStrings) {
        route.lineStrings.splice(expectedLineStrings);
        route.updated_at = new Date();
      }

      // Sanitize coordinates in all linestrings to ensure they contain valid numbers
      route.lineStrings.forEach((lineString, index) => {
        if (lineString?.lineStringGeometry?.coordinates) {
          const coords = lineString.lineStringGeometry.coordinates;
          const sanitized = sanitizeCoords(coords);

          // If coordinates were cleaned up, update the linestring
          if (sanitized.length !== coords.length || sanitized.length < 2) {
            lineString.lineStringGeometry.coordinates = sanitized.length >= 2 ? sanitized : [];
            route.updated_at = new Date();
          }
        }
      });

      // Remove linestrings with invalid or empty coordinates
      route.lineStrings = route.lineStrings.filter(lineString =>
        lineString?.lineStringGeometry?.coordinates?.length >= 2
      );

      if (applyJoinSpurTrims(route.lineStrings, route.markers)) {
        route.updated_at = new Date();
      }

      // Fill the placeholder and missing elevations the planner's fallbacks leave behind, so
      // the profile, the ascent and the saved geometry don't dive to 0 m at segment joins.
      const lines = route.lineStrings.map(ls => ls.lineStringGeometry.coordinates);
      if (repairRouteElevations(lines) > 0) route.updated_at = new Date();
      syncMarkerElevationsFromLines(route.markers, lines);
    });
  }
}

export default mutations
